<template>
  <div class="developer-information bg-background pb-5">
    <div
      class="mb-4 description d-flex justify-content-center text-center text-white align-items-center"
    >
      <h1>Training Sovereign LLMs with NVIDIA Nemotron</h1>
    </div>
    <b-container dir="ltr" class="text-left">
      <div class="d-flex justify-content-around mb-4">
        <a href="https://dicta.org.il"
          ><img src="@/assets/llm/image1.jpg" class="logo-item w-auto" style="max-height: 90px;"
        /></a>
        <a href="https://www.nvidia.com"
          ><img src="https://www.nvidia.com/content/dam/en-zz/Solutions/about-nvidia/logo-and-brand/01-nvidia-logo-vert-500x200-2c50-d@2x.png" class="logo-item w-auto" style="max-height: 100px;"
        /></a>
      </div>

      <section class="intro bg-white px-3 py-4 mx-auto mb-3 mt-4 shadow">
        <h1 class="text-center font-weight-bold mb-3">Training Sovereign LLMs with NVIDIA Nemotron and NVIDIA DGX Cloud Lepton</h1>
        <p class="text-muted mb-2 text-center">
          <strong>Authors:</strong> Shaltiel Shmidman, Amit Bleiweiss, Oleg Sudakov, Meriem Bendris, Asher Fredman
        </p>
        <div class="blue-sep"></div>

        <div class="intro">
          <p>
            Training sovereign Large Language Models (LLMs) for low-resource languages presents a complex set of challenges that go beyond technical optimization. These languages often suffer from limited high-quality digital text corpora, uneven data representation across dialects, and lower availability of linguistic tools like tokenizers or annotated datasets. Moreover, sociolinguistic diversity and the dominance of English-centric AI ecosystems exacerbate the difficulty of developing models that truly capture local nuance, cultural context, and idiomatic expression. Addressing these challenges requires novel data collection strategies, multilingual transfer learning, and effective tools for training and distributing compute across GPUs. In this blog, we show how to efficiently train a sovereign LLM, using Hebrew as a concrete example, using 
            <a href="https://www.nvidia.com/en-eu/ai-data-science/foundation-models/nemotron/" target="_blank">NVIDIA Nemotron</a>, 
            <a href="https://www.nvidia.com/en-us/ai-data-science/products/nemo/" target="_blank">NVIDIA NeMo</a>, and 
            <a href="https://www.nvidia.com/en-eu/data-center/dgx-cloud-lepton/" target="_blank">NVIDIA DGX Cloud Lepton</a>.
          </p>
        </div>
      </section>

      <section class="features bg-white px-3 py-4 mx-auto mb-3 shadow">
        <h2 class="text-center font-weight-bold">Nemotron as a base model for Sovereign LLM Training</h2>
        <div class="blue-sep"></div>
        <p>
          Oftentimes, the open-source models are a good enough base to start with and we can pre-train/fine-tune with the language specific data to improve its capabilities in that specific language. We chose 
          <a href="https://huggingface.co/nvidia/NVIDIA-Nemotron-Nano-12B-v2" target="_blank">NVIDIA Nemotron V2 12B</a> 
          as a base model as it addresses the following key parameters:
        </p>
        <ol>
          <li>
            <strong>Accuracy on both English & Hebrew:</strong> We want to start from a model with a robust English baseline, as well as a solid Hebrew foundation, and reinforce it further.
          </li>
          <li>
            <strong>Tokenization Compression Ratio (# of tokens per word):</strong> Most foundation models adopt byte-pair encoding (BPE) tokenizers, a statistical method that creates tokens based on frequency in a corpus. Due to the scarcity of low-resource languages in the training corpora of these models, the original tokenizer does not adequately cover the unique characters and morphology of the low-resource language. Without a sufficiently expressive tokenizer, common words are split into many sub-tokens, leading the model to struggle to represent the low-resource language efficiently. LLMs with suboptimal compression ratios are impractical - which means we can fit significantly less text in the context window, and requires more compute for both training and inference. Nemotron has a good ratio and hence makes it a natural choice.
          </li>
          <li>
            <strong>Open source data:</strong> Nemotron is one of the few open models which actually publish the 
            <a href="https://huggingface.co/nvidia/NVIDIA-Nemotron-Nano-12B-v2#public-datasets" target="_blank">training and validation data</a> 
            used in the pre-training stage so we can use that same data for continual pre-training of our enhanced Hebrew model. See 
            <a href="https://research.nvidia.com/labs/adlr/files/NVIDIA-Nemotron-Nano-2-Technical-Report.pdf" target="_blank">technical report</a> and this 
            <a href="https://huggingface.co/blog/nvidia/supercharge-ai-reasoning-with-nemotron-nano-2" target="_blank">Hugging Face article</a> 
            for additional details.
          </li>
        </ol>

        <p>
          The compression ratio on low resource languages in most models is very inefficient, to the point where we often will have fewer tokens than the number of letters in the alphabet. The figure below illustrates the relationship between the number of added tokens and the compression ratio in Hebrew, on tokenizers with very little Hebrew (e.g., Llama 3). Adding just 1,000 Hebrew tokens to the original tokenizer more than halves the compression rate, after which the gains from adding more tokens diminish significantly. The Nemotron model already includes enough Hebrew tokens that we are past the knee of the curve.
        </p>

        <img src="@/assets/llm/nemotron-compression-knee.png" class="d-block mx-auto img-fluid" alt="Compression ratio graph" />
        <p class="text-center text-muted mb-4" style="font-size: 0.9em; font-style: italic;">
          Compression ratio (lower is better) when adding Hebrew tokens to the Llama tokenizer. Efficiency gains are significant up to ~1,000 tokens, after which the benefit diminishes.
        </p>

        <p>
          Here we show the code that we used to compute the compression ratio of two popular models (NVIDIA-Nemotron-Nano-V2 and Llama-3) on the public 
          <a href="https://huggingface.co/datasets/dicta-il/hebrew-space-restoration-corpus" target="_blank">Dicta Hebrew Space Restoration Corpus</a>:
        </p>

        <div class="code-block-wrapper mb-4">
          <div class="code-header">
            <span class="code-language">Python</span>
            <button class="copy-btn" @click="copyCode($event, 'code1')">
              <i class="fas fa-copy"></i> Copy
            </button>
          </div>
          <pre><code id="code1" class="language-python"># load in a corpus of Hebrew sentences
from datasets import load_dataset
from tqdm import tqdm
from transformers import AutoTokenizer

# public dataset containing ~6,000 Hebrew sentences. 
ds = load_dataset("dicta-il/hebrew-space-restoration-corpus", split="test")["output"]
# swap out the model name here for other models
model_name = 'nvidia/NVIDIA-Nemotron-Nano-12B-v2-Base'
tokenizer = AutoTokenizer.from_pretrained(model_name)

# We want to compute here: the number of tokens per word (the compression ratio)
total_words, total_tokens = 0, 0
for text in tqdm(ds):
	total_words += len(text.split())
	total_tokens += len(tokenizer.encode(text))
print(f'tokens / word:   {total_tokens / total_words:.2f}')</code></pre>
        </div>

        <p>
          Comparing the output of 
          <a href="https://huggingface.co/nvidia/NVIDIA-Nemotron-Nano-12B-v2" target="_blank">Nemotron Nano V2 12B</a> 
          and Llama 3 8B in the table below, we see Nemotron has a lower compression ratio - less tokens per word enable more efficient representation of the text. This also enables us to fit more tokens in the context window.
        </p>

        <img src="@/assets/llm/nemotron-compression-ratio-comp.png" class="mb-4 d-block mx-auto img-fluid" alt="Comparison table" />
      </section>

      <section class="bg-white px-3 py-4 mx-auto mb-3 shadow">
        <h2 class="text-center font-weight-bold">Data Preparation</h2>
        <div class="blue-sep"></div>

        <p>
          Hebrew is a low-resource language, so we want to train on as much data as we can get our hands on. For Hebrew we collected a total of 90B tokens, using the Nemotron tokenizer, after heavy filtering & deduping for high quality data. For English, we want to make sure the model doesn't suffer catastrophic forgetting, so we sample data from the 
          <a href="https://huggingface.co/datasets/nvidia/Nemotron-CC-v2" target="_blank">Nemotron-CC dataset</a>, 
          using a similar mixture to that used in Phase 3:
        </p>

        <img src="@/assets/llm/nemotron-data-mixture.png" class="d-block mx-auto img-fluid" alt="Data mixture" />
        <p class="text-center text-muted mb-4" style="font-size: 0.9em; font-style: italic;">
          Figure taken from the <a href="https://arxiv.org/abs/2508.14444" target="_blank">Nemotron technical paper</a>.
        </p>

        <p>
          After experimentation, we set the ratio to be 25% English, 75% Hebrew - this ratio allowed the model to maintain its knowledge of English with minimal degradation.
        </p>

        <p>
          Preparing the data: We pre-tokenize into an IndexedDataset so that the dataloader will have near-zero delays, and will be able to pull the info using a numpy memap.
        </p>

        <ul>
          <li>Download your data in .jsonl files, place them in a directory, we'll call it $DATA_DIR</li>
          <li>Pull & launch the container:</li>
        </ul>

        <div class="code-block-wrapper mb-4">
          <div class="code-header">
            <span class="code-language">Shell</span>
            <button class="copy-btn" @click="copyCode($event, 'code2')">
              <i class="fas fa-copy"></i> Copy
            </button>
          </div>
          <pre><code id="code2" class="language-bash">docker pull nvcr.io/nvidia/nemo:25.09
docker run -it --rm -v .:/workspace -v $DATA_DIR:/data --shm-size=8g --ulimit memlock=-1 --ulimit stack=67108864 -w /workspace nvcr.io/nvidia/nemo:25.07.gpt_oss bash -c "python /opt/NeMo/scripts/nlp_language_modeling/preprocess_data_for_megatron.py \
  --input ./data \
  --keep-newlines \
  --tokenizer-library huggingface \
  --tokenizer-type nvidia/NVIDIA-Nemotron-Nano-12B-v2-Base \
  --append-eod \
  --output-prefix ./data/tokenized_data \
  --workers 128 \
  --preproc-folder \
  --files-filter '*' \
  --log-interval 50000</code></pre>
        </div>

        <p>This produces two files for every dataset, which can then be used for training with the <a href="https://github.com/NVIDIA-NeMo/" target="_blank">NVIDIA NeMo framework</a>. You can produce as many pairs as you want, and then interleave/blend them in real time very easily.</p>
        <ul>
          <li>tokenized_data_text_document.bin</li>
          <li>tokenized_data_text_document.idx</li>
        </ul>
      </section>

      <section class="bg-white px-3 py-4 mx-auto mb-3 shadow">
        <h2 class="text-center font-weight-bold">Training with NVIDIA DGX Cloud Lepton</h2>
        <div class="blue-sep"></div>

        <p>
          For training compute, we use 
          <a href="https://developer.nvidia.com/blog/introducing-nvidia-dgx-cloud-lepton-a-unified-ai-platform-built-for-developers/" target="_blank">NVIDIA DGX Cloud Lepton</a>, 
          a unified AI platform that connects developers to tens of thousands of GPUs from a global network of cloud providers. It addresses a critical need: accelerating AI developer productivity by providing access to GPU capacity and AI services across the NVIDIA compute ecosystem. It integrates seamlessly with the NVIDIA software stack such as NeMo Framework, enabling developers to build, train, and deploy AI applications quickly and Scale.
        </p>

        <p>
          For our training environment, we set up 10 8xH200 nodes and selected the 
          <a href="https://catalog.ngc.nvidia.com/orgs/nvidia/containers/nemo?version=25.09" target="_blank">latest NeMo Framework container</a> 
          from 
          <a href="https://catalog.ngc.nvidia.com/" target="_blank">NVIDIA NGC Catalog</a>. 
          Creating the training script, and integrating the flow with 
          <a href="https://github.com/NVIDIA-NeMo/Run" target="_blank">NeMo Run</a> 
          for streamlining training experimentation, is extremely easy:
        </p>

        <div class="code-block-wrapper mb-4">
          <div class="code-header">
            <span class="code-language">Python</span>
            <button class="copy-btn" @click="copyCode($event, 'code3')">
              <i class="fas fa-copy"></i> Copy
            </button>
          </div>
          <pre><code id="code3" class="language-python">import nemo_run as run
from nemo.collections import llm
from nemo.collections.llm.recipes.finetune_default import nemo_resume
from nemo.collections.llm.gpt.data.pre_training import PreTrainingDataModule
from nemo.collections.common.tokenizers.huggingface.auto_tokenizer import AutoTokenizer

if __name__ == '__main__':
    num_nodes = 2
    gpus_per_node = 8

    pretrain = llm.nemotron_nano_12b_v2.pretrain_recipe(
        name="hebrew_nano_v2_training",
        dir='./checkpoints',
        num_nodes=num_nodes,
        num_gpus_per_node=gpus_per_node,
        tensor_parallelism=1,
        max_steps=20_000,
        seq_length=4096
    )
    tokenizer = run.Config(AutoTokenizer,
        pretrained_model_name='nvidia/NVIDIA-Nemotron-Nano-9B-v2'
    )
    pretrain.data = run.Config(PreTrainingDataModule, 
         paths=['./data/tokenized_data_text_document'],
         tokenizer=tokenizer, 
         seq_length=4096, 
         micro_batch_size=1,
         global_batch_size=256,
         reset_position_ids=True,
         reset_attention_mask=True,
         eod_mask_loss=False,
         split="998,1,1"
     )
    pretrain.resume = nemo_resume('nvidia/NVIDIA-Nemotron-Nano-12B-v2-Base')
    run.run(pretrain, executor=lepton_executor(nodes=nodes, gpus_per_node=gpus_per_node, container_image='nvcr.io/nvidia/nemo:25.09'))</code></pre>
        </div>

        <p>
          After configuring NeMo-Run, the next step is to execute it. Nemo-Run decouples configuration from execution, allowing you to configure a function or task once and then execute it across multiple environments. Each execution of a single configured task requires an executor. Since we train on NVIDIA DGX Cloud Lepton, we need to define a 
          <a href="https://docs.nvidia.com/nemo/run/latest/guides/execution.html#leptonexecutor" target="_blank">LeptonExecutor</a> 
          which will integrate with the compute cluster to launch distributed jobs:
        </p>

        <div class="code-block-wrapper mb-4">
          <div class="code-header">
            <span class="code-language">Python</span>
            <button class="copy-btn" @click="copyCode($event, 'code4')">
              <i class="fas fa-copy"></i> Copy
            </button>
          </div>
          <pre><code id="code4" class="language-python">import nemo_run as run
import os

def lepton_executor(nodes: int, gpus_per_node: int, container_image: str):
    # Ensure these are set correctly for your DGX Cloud environment
    # You might fetch these from environment variables or a config file
    resource_shape = f"gpu.{gpus_per_node}xh200"
    node_group = "nv-...."  # The node group to run the job in
    nemo_run_dir = "/path/to/nemo_run"  # The NeMo-Run directory where experiments are saved
    storage_path = "/" # The remote storage directory to mount in jobs
    mount_path = "/mnt/shared" # The path where the remote storage directory will be mounted inside the container
    executor = run.LeptonExecutor(
        resource_shape=resource_shape,
        node_group=node_group,
        container_image=container_image,
        nodes=nodes,
        nemo_run_dir=nemo_run_dir,
        gpus_per_node=gpus_per_node,
        mounts=[], # fill here the shared storage mounts
        env_vars={}, # fill here the environment variables to pass along
    )
    return executor</code></pre>
        </div>

        <p>From here, you can launch a new job in Lepton, and the distributed training is running! You can start the job in two ways:</p>

        <ol>
          <li>Directly from your local machine - from your shell make sure to log in to the lepton CLI ("lep login"), and then run your script ("python script.py")</li>
          <li>Using the DGX Cloud Lepton "Batch Jobs" interface online - simply launch a new job (see figure below), with the run command "python script.py". In order for this to run, it is important to add the environment variable to the job "LEPTON_WORKSPACE_TOKEN" so that your script is authenticated to launch the distributed training in the cloud.</li>
        </ol>

        <img src="@/assets/llm/nemotron-lepton-job.png" class="mb-4 d-block mx-auto img-fluid" alt="Batch jobs interface" />

        <p>By default it only outputs Tensorboard, you can add tracking with WandB by adding this line:</p>

        <div class="code-block-wrapper mb-4">
          <div class="code-header">
            <span class="code-language">Python</span>
            <button class="copy-btn" @click="copyCode($event, 'code5')">
              <i class="fas fa-copy"></i> Copy
            </button>
          </div>
          <pre><code id="code5" class="language-python">from nemo.collections.llm.recipes.log.default import default_log, wandb_logger
pretrain.log = default_log(dir='./checkpoints, name="hebrew_nano_v2_training", wandb_logger=wandb_logger(project='hebrew_nano_v2_training'))</code></pre>
        </div>

        <p>That's it! Let the model train, and track performance.</p>
      </section>

      <section class="bg-white px-3 py-4 mx-auto mb-3 shadow">
        <h2 class="text-center font-weight-bold">Analyzing the Results</h2>
        <div class="blue-sep"></div>

        <p>Here you can see the loss graph for a total of ~100k steps of the train, a complete CPT cycle on 130B tokens (with a global batch size of 320):</p>
        <img src="@/assets/llm/nemotron-loss-graph.png" class="mb-4 d-block mx-auto img-fluid" alt="Loss graph" />

        <p>In addition, we also evaluate the model on the benchmarks from the <a href="https://huggingface.co/spaces/hebrew-llm-leaderboard/leaderboard">Hebrew-LLM-Leaderboard</a>, and you can see here the continuous average gains, when comparing to the performance on the base model:</p>
        <img src="@/assets/llm/nemotron-hebrew-perf.png" class="mb-4 d-block mx-auto img-fluid" alt="Hebrew leaderboard results" />
        
        <p>Finally, we also track the performance in English to make sure we don't suffer from catastrophic forgetting. Since the Nemotron Nano V2 is one of the few open models which actually publish the training data used in the Pre-Training stage, we can use that same data as the English part of our data mix for the continual pre-training (CPT) stage. As mentioned above, the English data comprised 25% of the data mix, totalling approximately 30B tokens. For the English metrics, we use the following three tasks: CommonSense, WinoGrande, and Arc-Challenge.</p>

        <p>Here below you can see the performance comparison on both the Hebrew benchmarks and on English benchmarks, before and after the CPT training:</p>
        
        <div class="table-responsive mb-4 mx-auto d-flex flex-column align-items-center" style="max-width: 700px;">
          <table class="table table-sm table-bordered text-center" style="font-size: 0.9em;">
            <thead class="thead-light">
              <tr>
                <th>Model</th>
                <th>SNLI</th>
                <th>QA</th>
                <th>Sentiment</th>
                <th>Winograd</th>
                <th>Translation</th>
                <th>IL-Facts</th>
                <th>Average</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Base Model</strong></td>
                <td>71.4</td>
                <td>66.1</td>
                <td>64.5</td>
                <td>64.0</td>
                <td>21.5</td>
                <td>34.9</td>
                <td>53.7</td>
              </tr>
              <tr class="table-primary">
                <td><strong>After CPT</strong></td>
                <td>80.5</td>
                <td>77.8</td>
                <td>72.4</td>
                <td>79.5</td>
                <td>33.9</td>
                <td>54.8</td>
                <td>66.5</td>
              </tr>
              <tr>
                <td><strong>Improvement</strong></td>
                <td style="color: #28a745; font-weight: 600;">+9.1</td>
                <td style="color: #28a745; font-weight: 600;">+11.7</td>
                <td style="color: #28a745; font-weight: 600;">+7.9</td>
                <td style="color: #28a745; font-weight: 600;">+15.5</td>
                <td style="color: #28a745; font-weight: 600;">+12.4</td>
                <td style="color: #28a745; font-weight: 600;">+19.9</td>
                <td style="color: #28a745; font-weight: 700;">+12.8</td>
              </tr>
            </tbody>
          </table>
          <p class="text-center text-muted" style="font-size: 0.85em; font-style: italic;">
            Hebrew benchmark performance comparison: Nemotron-Nano-12B-v2 before and after Continual Pre-Training (CPT)
          </p>
        </div>

        <div class="table-responsive mb-4 mx-auto d-flex flex-column align-items-center" style="max-width: 550px;">
          <table class="table table-sm table-bordered text-center" style="font-size: 0.9em;">
            <thead class="thead-light">
              <tr>
                <th>Model</th>
                <th>CommonSense</th>
                <th>WinoGrande</th>
                <th>Arc-Challenge</th>
                <th>Average</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Base Model</strong></td>
                <td>79.4</td>
                <td>75.7</td>
                <td>64.7</td>
                <td>73.3</td>
              </tr>
              <tr class="table-primary">
                <td><strong>After CPT</strong></td>
                <td>80.7</td>
                <td>76.2</td>
                <td>64.4</td>
                <td>73.8</td>
              </tr>
              <tr>
                <td><strong>Improvement</strong></td>
                <td style="color: #28a745; font-weight: 600;">+1.2</td>
                <td style="color: #28a745; font-weight: 600;">+0.5</td>
                <td style="color: #dc3545; font-weight: 600;">-0.3</td>
                <td style="color: #28a745; font-weight: 700;">+0.5</td>
              </tr>
            </tbody>
          </table>
          <p class="text-center text-muted" style="font-size: 0.85em; font-style: italic;">
            English benchmark performance comparison: Nemotron-Nano-12B-v2 before and after Continual Pre-Training (CPT)
          </p>
        </div>
        
      </section>

      <section class="bg-white px-3 py-4 mx-auto mb-3 shadow">
        <h2 class="text-center font-weight-bold">Next Steps</h2>
        <div class="blue-sep"></div>
        <p>
        After establishing a strong base model with enhanced Hebrew capabilities, the next stage was to run post-training. This involved Supervised Fine-Tuning (SFT) on chat data, followed by Reinforcement Learning techniques such as Direct Preference Optimization (DPO) and Group Relative Policy Optimization (GRPO) using the <a href="https://github.com/NVIDIA-NeMo/RL" target="_blank">NeMo-RL</a> library. We completed this process and the final model is available here: 
          <a href="https://huggingface.co/dicta-il/DictaLM-3.0-Nemotron-12B-Instruct" target="_blank">&#129303; DictaLM-3.0-Nemotron-12B-Instruct</a>.
        </p>
        <p>
          For more details, see the 
          <a href="/publications/DictaLM_3_0___Techincal_Report.pdf" target="_blank">technical report</a>.
        </p>
      </section>

      <section class="bg-white px-3 py-4 mx-auto mb-3 shadow d-flex flex-column align-items-center">
        <h2 class="text-center font-weight-bold">Get Started</h2>
        <div class="blue-sep"></div>
        <p>
          To summarize, Nemotron Nano 2 12B is a great base model for training sovereign LLMs. Its strong foundation for non-English languages, efficient token compression ratio, and open source training data make it a great foundation for training low-resource languages LLMs. To get started, download the model from 
          <a href="https://huggingface.co/nvidia/NVIDIA-Nemotron-Nano-12B-v2" target="_blank">Hugging Face</a> 
          and create your own sovereign LLM by following the recipe in this blog. Explore additional open models and datasets in the Nemotron family 
          <a href="https://www.nvidia.com/en-us/ai-data-science/foundation-models/nemotron/" target="_blank">here</a>.
        </p>
        <p>
          DGX Cloud Lepton is an easy-to-use intuitive platform to run your large scale training workloads and accelerates AI developer productivity by providing unified access to GPU capacity and AI services across the NVIDIA compute ecosystem. Explore DGX Cloud Lepton in 
          <a href="https://developer.nvidia.com/dgx-cloud/get-lepton" target="_blank">Early Access</a>.
        </p>
      </section>

      <div
        class="alert alert-primary mx-auto border border-primary text-center"
      >
        <p class="mb-0">
          <strong>Authors:</strong> Shaltiel Shmidman, Amit Bleiweiss, Oleg Sudakov, Meriem Bendris, Asher Fredman
        </p>
        <hr />
        <p class="mb-0">
          Released by DICTA in collaboration with NVIDIA
        </p>
        <p class="mb-0">
          For questions, please contact Shaltiel Shmidman, Lead Deep Learning Developer at Dicta, at: shaltiel@dicta.org.il
        </p>
      </div>
    </b-container>
  </div>
</template>

<script>
export default {
  name: "NemotronBlog",
  mounted() {
    this.loadPrism()
  },
  methods: {
    loadPrism() {
      // Load Prism CSS
      if (!document.getElementById('prism-css')) {
        const link = document.createElement('link')
        link.id = 'prism-css'
        link.rel = 'stylesheet'
        link.href = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism-tomorrow.min.css'
        document.head.appendChild(link)
      }

      // Load Prism JS
      if (!window.Prism) {
        const script = document.createElement('script')
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/prism.min.js'
        script.onload = () => {
          this.loadPrismLanguages()
        }
        document.head.appendChild(script)
      } else {
        this.loadPrismLanguages()
      }
    },
    loadPrismLanguages() {
      // Load Python language support
      if (!document.getElementById('prism-python')) {
        const pythonScript = document.createElement('script')
        pythonScript.id = 'prism-python'
        pythonScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/components/prism-python.min.js'
        pythonScript.onload = () => {
          this.highlightCode()
        }
        document.head.appendChild(pythonScript)
      }

      // Load Bash language support
      if (!document.getElementById('prism-bash')) {
        const bashScript = document.createElement('script')
        bashScript.id = 'prism-bash'
        bashScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/components/prism-bash.min.js'
        bashScript.onload = () => {
          this.highlightCode()
        }
        document.head.appendChild(bashScript)
      }

      // Highlight after a short delay to ensure all scripts are loaded
      setTimeout(() => {
        this.highlightCode()
      }, 500)
    },
    highlightCode() {
      if (window.Prism) {
        window.Prism.highlightAll()
      }
    },
    copyCode(event, codeId) {
      const codeElement = document.getElementById(codeId)
      const code = codeElement.textContent
      
      navigator.clipboard.writeText(code).then(() => {
        const btn = event.currentTarget
        const originalText = btn.innerHTML
        btn.innerHTML = '<i class="fas fa-check"></i> Copied!'
        btn.classList.add('copied')
        
        setTimeout(() => {
          btn.innerHTML = originalText
          btn.classList.remove('copied')
        }, 2000)
      })
    }
  }
}
</script>

<style scoped lang="scss">
.logo-item {
  max-height: 100px;
}

li {
  margin-bottom: 10px;
}

.alert,
section {
  border-radius: 8px;
}

.code-block-wrapper {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #2d2d2d;
  
  .code-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 15px;
    background: #1e1e1e;
    border-bottom: 1px solid #3d3d3d;
    
    .code-language {
      color: #9cdcfe;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
    }
    
    .copy-btn {
      background: #0e639c;
      color: white;
      border: none;
      padding: 5px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
      transition: all 0.2s;
      
      &:hover {
        background: #1177bb;
      }
      
      &.copied {
        background: #16825d;
      }
      
      i {
        margin-right: 5px;
      }
    }
  }
  
  pre {
    margin: 0;
    padding: 15px;
    background: #2d2d2d;
    overflow-x: auto;
    
    code {
      font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
      font-size: 13px;
      line-height: 1.5;
      color: #d4d4d4;
    }
  }
}

@media (min-width: 992px) {
  .developer-information {
    table,
    .alert,
    section {
      max-width: 85%;
      img.table-img {
        max-width: 75% !important;
      }
    }
  }
}

section img.img-fluid {
  max-width: 70% !important;
  height: auto !important;
  display: block !important;
  margin-left: auto !important;
  margin-right: auto !important;
}

@media (max-width: 768px) {
  section img.img-fluid {
    max-width: 95% !important;
  }
}
</style>
